import { apiErrorMessage, customFetch } from '@agos/api-client'
import {
  centsToApiString,
  courierBalanceOf,
  courierBalancePath,
  courierSettlementsPath,
  formatCents,
  formatTime,
  parseMoneyToCents,
  paymentLabel,
  restaurantSettleNote,
  T,
  tt,
} from '@agos/shared'
import { ConfirmDialog, toast } from '@agos/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bike, HandCoins, Undo2, UserPlus } from 'lucide-react'
import { useState } from 'react'

import type { BoardOrder } from './board'
import { cardAtTheDoorNote, courierStateOf, OFFER_PATH, type OfferAction } from './courier-board'

/**
 * External couriers on the dispatch board - `FRONTEND.md` §9.786, on BACKEND.md §10A.438, owner O139. The rules
 * are `courier-board.ts`; this draws them.
 */

/** *Čaká na externého šoféra*, or *externý · vzal 18:02*, beside the order - nothing for any other order. */
export function CourierStateBadge({ order }: { order: BoardOrder }) {
  const state = courierStateOf(order)
  if (state === null) return null
  const reward =
    order.driverReward === null || order.driverReward === undefined
      ? ''
      : ` · ${tt('odmena')} ${formatCents(parseMoneyToCents(order.driverReward))}`
  if (state === 'waiting') {
    return (
      <span
        data-courier-state="waiting"
        className="flex items-center gap-1 text-[10px] font-bold rounded px-1 py-0.5 border leading-none bg-sky-500/15 text-sky-500 border-sky-500/30 whitespace-nowrap"
      >
        <Bike className="w-2.5 h-2.5" />
        {tt('Čaká na externého šoféra')}
        {reward}
      </span>
    )
  }
  return (
    <span
      data-courier-state="claimed"
      title={`${tt('Externý šofér si objednávku vzal o')} ${formatTime(order.claimedAt ?? '')}`}
      className="flex items-center gap-1 text-[10px] font-bold rounded px-1 py-0.5 border leading-none bg-sky-500/10 text-sky-400 border-sky-500/20 whitespace-nowrap"
    >
      <Bike className="w-2.5 h-2.5" />
      {tt('externý')} · {tt('vzal')} {formatTime(order.claimedAt ?? '')}
      {reward}
    </span>
  )
}

/**
 * *Ponúknuť externým* / *Stiahnuť ponuku*, or why it cannot be offered. The server answers both writes; a refusal
 * (the order took a driver in the meantime, or another screen offered it) is said in its own words.
 */
export function CourierOfferAction({
  order,
  action,
  onDone,
}: {
  order: BoardOrder
  action: OfferAction
  onDone: () => void
}) {
  const [busy, setBusy] = useState(false)
  if (action === null) return null
  if (action === 'card-at-door') {
    return (
      <span
        data-courier-offer-refused={order.orderNumber}
        title={cardAtTheDoorNote()}
        className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground"
      >
        <Bike className="w-3 h-3" />
        <T>Externému šoférovi nie (karta pri dverách)</T>
      </span>
    )
  }
  const offering = action === 'offer'
  const send = async () => {
    setBusy(true)
    try {
      await customFetch<unknown>(OFFER_PATH(order.id ?? 0), { method: offering ? 'POST' : 'DELETE' })
      toast({
        title: offering
          ? `${tt('Ponúknuté externým šoférom')} #${order.orderNumber ?? order.id ?? '?'}`
          : `${tt('Ponuka stiahnutá')} #${order.orderNumber ?? order.id ?? '?'}`,
      })
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: offering ? tt('Ponúknuť sa nepodarilo') : tt('Stiahnuť ponuku sa nepodarilo'),
        description: apiErrorMessage(caught, tt('Skúste to znova.')),
      })
    }
    setBusy(false)
    onDone()
  }
  return (
    <button
      type="button"
      data-courier-offer={offering ? 'offer' : 'withdraw'}
      disabled={busy}
      onClick={() => void send()}
      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border bg-sky-500/10 text-sky-500 border-sky-500/30 hover:bg-sky-500/20 transition-colors disabled:opacity-50"
    >
      {offering ? <UserPlus className="w-3 h-3" /> : <Undo2 className="w-3 h-3" />}
      {offering ? <T>Ponúknuť externým</T> : <T>Stiahnuť ponuku</T>}
    </button>
  )
}

/**
 * The settling dialog at *Vyraziť* with an external courier - the owner's answer 2, *settled at pickup, netted*.
 *
 * The orders just sent, the reward of each and the cash he will collect, and **one amount**: what he pays the
 * restaurant now, or what it pays him. *Zaplatené* records it as a settlement of exactly the balance, so they are
 * square; *Neskôr* records nothing and the balance stays for the next pickup or the report.
 *
 * **The amount is the whole balance, not this run's**, because an earlier *Neskôr* is still owed - and when the two
 * differ the dialog says so, so nobody hands over a figure they cannot account for.
 */
export function CourierSettleDialog({
  driverId,
  driverName,
  orderIds,
  onClose,
}: {
  driverId: number
  driverName: string
  orderIds: readonly number[]
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  const read = useQuery({
    queryKey: ['courier-balance', driverId],
    queryFn: () => customFetch<unknown>(courierBalancePath(driverId)),
  })
  const balance = courierBalanceOf(read.data)
  const sent = balance === null ? [] : balance.today.filter((order) => orderIds.includes(order.id))
  const runNetCents = sent.reduce((sum, order) => sum + order.cashCents - order.rewardCents, 0)
  const owes = balance?.owesCents ?? 0

  const settle = async () => {
    if (balance === null || owes === 0) {
      onClose()
      return
    }
    setSaving(true)
    try {
      await customFetch<unknown>(courierSettlementsPath(driverId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: Number(centsToApiString(owes)) }),
      })
      toast({ title: tt('Vyrovnanie zapísané'), description: `${driverName}: ${restaurantSettleNote(owes)}` })
      void queryClient.invalidateQueries({ queryKey: ['courier-balance', driverId] })
      onClose()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Vyrovnanie sa nepodarilo zapísať'),
        description: apiErrorMessage(caught, tt('Skúste to znova alebo ho zapíšte v Externý šofér.')),
      })
    }
    setSaving(false)
  }

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      onConfirm={() => void settle()}
      title={`${tt('Vyrovnanie so šoférom')} ${driverName}`}
      icon={<HandCoins className="w-5 h-5" />}
      confirmLabel={balance !== null && owes === 0 ? tt('Zavrieť') : tt('Zaplatené')}
      cancelLabel={tt('Neskôr')}
      loading={saving}
      confirmDisabled={read.isLoading || balance === null}
    >
      <div className="space-y-2 text-sm" data-courier-settle={driverId}>
        {read.isLoading ? (
          <p className="text-muted-foreground">
            <T>Načítavam, čo si s prevádzkou dlžia…</T>
          </p>
        ) : balance === null ? (
          <p data-courier-settle-failed className="text-red-500">
            <T>Zostatok šoféra sa nepodarilo načítať. Vyrovnajte ho neskôr v Externý šofér.</T>
          </p>
        ) : (
          <>
            {sent.length > 0 && (
              <ul className="space-y-1">
                {sent.map((order) => (
                  <li key={order.id} data-courier-settle-order={order.orderNumber} className="flex justify-between gap-2">
                    <span>
                      #{order.orderNumber} · {paymentLabel(order.paymentMethod)}
                      {order.cashCents > 0 && ` ${formatCents(order.cashCents)}`}
                    </span>
                    <span className="text-muted-foreground whitespace-nowrap">
                      {tt('odmena')} {formatCents(order.rewardCents)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p
              data-courier-settle-amount={owes}
              className="text-base font-black"
            >
              {restaurantSettleNote(owes)}
            </p>
            {sent.length > 0 && owes !== runNetCents && (
              <p data-courier-settle-earlier className="text-xs text-muted-foreground">
                {tt('Vrátane zostatku z predchádzajúcich objednávok')}: {formatCents(owes - runNetCents)}
              </p>
            )}
          </>
        )}
      </div>
    </ConfirmDialog>
  )
}
